import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Avatar,
  Alert,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Search as SearchIcon,
  Computer as ComputerIcon,
  Language as WebsiteIcon,
  Notifications as NotificationsIcon,
  VolumeOff as SilenceIcon,
} from '@mui/icons-material';

// Tab components
import { ExportersTab, WebsitesTab, NotificationsTab, SilencesTab } from './tabs';

// Local components
import { MoreActionsMenu } from './components/MoreActionsMenu';
import { AlertsDrawer } from './components/AlertsDrawer';

// Dialogs
import { AddExporterDialog, AddExporterFormData } from './dialogs/AddExporterDialog';
import { EditExporterDialog } from './dialogs/EditExporterDialog';
import { AddWebsiteDialog, AddWebsiteFormData } from './dialogs/AddWebsiteDialog';
import { EditWebsiteDialog } from './dialogs/EditWebsiteDialog';
import { DeleteConfirmationDialog } from './dialogs/DeleteConfirmationDialog';
import { InstallerGeneratorDialog, InstallerFormData } from './dialogs/InstallerGeneratorDialog';
import { AddNotificationChannelDialog, AddNotificationChannelFormData } from './dialogs/AddNotificationChannelDialog';
import { EditNotificationChannelDialog, EditNotificationChannelFormData } from './dialogs/EditNotificationChannelDialog';
import { CreateSilenceDialog } from './dialogs/CreateSilenceDialog';
import { ExtendSilenceDialog } from './dialogs/ExtendSilenceDialog';
import { AlertRuleDialog } from './dialogs/AlertRuleDialog';

// Hooks
import { useExporters } from '../hooks/useExporters';
import { useCategories } from '../hooks/useCategories';
import { useCustomers } from '../hooks/useCustomers';
import { useWebsites } from '../hooks/useWebsites';
import { useNotificationChannels } from '../hooks/useNotificationChannels';
import { useSilences } from '../hooks/useSilences';
import useNotificationProvider from '../../../../hooks/notificationBar/useNotificationProvider';

// API Services
import { exporterService } from '../../../../api/cansense/exporterApi';
import { websiteApi } from '../../../../api/cansense/websiteApi';
import { installerService } from '../../../../api/cansense/installerApi';
import { notificationChannelApi, CreateNotificationChannelData, CanmailList } from '../../../../api/cansense/notificationChannelApi';
import { ruleService } from '../../../../api/cansense/ruleApi';

// Routing
import { useSearchParams } from 'react-router-dom';

// Utilities
import { extractApiError } from '../utils/errorUtils';

// Types
import {
  Exporter as ManagementExporter,
  Website as ManagementWebsite,
  NotificationChannel as ManagementNotificationChannel,
  MenuAction,
  BackendAlertRule,
  AlertRuleFormData,
} from './types';
import { Exporter } from '../types/exporter';
import { Website as ApiWebsite } from '../types/website';
import { NotificationChannel as ApiNotificationChannel } from '../../../../api/cansense/notificationChannelApi';

// ============================================================================
// Type Adapters - Convert API types to ManagementV2 UI types
// ============================================================================

/**
 * Adapt exporter from API format to ManagementV2 UI format
 */
const adaptExporter = (exporter: Exporter): ManagementExporter => ({
  id: exporter.id,
  name: exporter.prometheus_job || `exporter-${exporter.id}`,
  displayName: exporter.display_name || exporter.name,
  status: (exporter.status === 'unknown' || exporter.status === 'pending' || exporter.status === 'disabled' ? 'down' : exporter.status) as 'up' | 'down' | 'warning',
  type: exporter.category_name || 'Unknown',
  host: exporter.target_host,
  lastSeen: exporter.last_scrape
    ? formatLastSeen(new Date(exporter.last_scrape))
    : 'Never',
  enabled: exporter.enabled,
  collectionMethod: exporter.collection_method || 'pull',
});

/**
 * Adapt website from API format to ManagementV2 UI format
 */
const adaptWebsite = (website: ApiWebsite): ManagementWebsite => ({
  id: website.id,
  name: website.name,
  url: website.url,
  status: website.status as 'up' | 'down',
  responseTime: website.response_time_ms ?? null,
  uptime: website.uptime_24h ?? 0,
  customerId: website.customer_id ?? null,
  checkInterval: website.check_interval || '60s',
  enabled: website.enabled ?? true,
});

/**
 * Adapt notification channel from API format to ManagementV2 UI format
 */
const adaptNotificationChannel = (channel: ApiNotificationChannel): ManagementNotificationChannel => {
  const normalizedType = channel.type === 'canmail_list' as string ? 'canmail' : channel.type;

  return {
    id: channel.id,
    type: normalizedType as ManagementNotificationChannel['type'],
    name: channel.name,
    value: channel.value || '',
    canmailListId: normalizedType === 'canmail'
      ? parseInt(channel.value, 10) || undefined
      : undefined,
    memberCount: channel.member_count,
    watchAll: channel.watch_all ?? false,
    watchExporters: (channel.watch_exporter_ids ?? []).map(String),
    severities: channel.severities ?? ['critical'],
    verified: channel.verified ?? false,
  };
};

/**
 * Format last seen timestamp
 */
const formatLastSeen = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// ============================================================================
// ManagementV2 Component
// ============================================================================

export interface ManagementV2Props {
  initialTab?: number;
}

/**
 * ManagementV2 - Main management page with three tabs.
 *
 * Tab 0: Exporters - Table of monitored servers/network devices
 * Tab 1: Websites - Table of monitored websites
 * Tab 2: Notifications - Channel-based notification configuration
 *
 * Features:
 * - Search bar for exporters/websites (hidden on Notifications tab)
 * - MoreActionsMenu for quick actions on exporters/websites
 * - AlertsDrawer for per-exporter alert rule management
 * - Full CRUD operations via real APIs
 */
export function ManagementV2({ initialTab = 0 }: ManagementV2Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Notification provider
  const { notifySuccess, notifyError, notifyWarning } = useNotificationProvider();

  // Data hooks
  const {
    exporters: rawExporters,
    loading: exportersLoading,
    error: exportersError,
    refetch: refetchExporters,
  } = useExporters();

  const { categories } = useCategories();
  const { customers } = useCustomers();

  const {
    websites: rawWebsites,
    summary: websiteSummary,
    loading: websitesLoading,
    error: websitesError,
    refetch: refetchWebsites,
  } = useWebsites();

  const {
    channels: rawChannels,
    loading: channelsLoading,
    error: channelsError,
    refetch: refetchChannels,
    createChannel,
    updateChannel,
    deleteChannel: deleteChannelApi,
    testChannel,
  } = useNotificationChannels();

  const {
    silences,
    loading: silencesLoading,
    error: silencesError,
    refetch: refetchSilences,
    createSilence,
    deleteSilence,
    quickSilence,
    extendSilence,
  } = useSilences({ status: 'active' });

  // CANmail lists for notification channel dialogs
  const [canmailLists, setCanmailLists] = useState<CanmailList[]>([]);

  useEffect(() => {
    const fetchCanmailLists = async () => {
      try {
        const lists = await notificationChannelApi.getCanmailLists();
        setCanmailLists(lists);
      } catch (err) {
        console.error('Failed to fetch CANmail lists:', err);
      }
    };
    fetchCanmailLists();
  }, []);

  // Transform API data to UI format
  const exporters = rawExporters.map(adaptExporter);
  const websites = rawWebsites.map(adaptWebsite);
  const notificationChannels = rawChannels.map(adaptNotificationChannel);

  // Auto-open edit dialog when navigated with ?editExporter=<id>
  useEffect(() => {
    const editId = searchParams.get('editExporter');
    if (editId && exporters.length > 0) {
      const target = exporters.find((e) => e.id === parseInt(editId, 10));
      if (target) {
        setActiveTab(0);
        setEditingExporter(target);
        setEditExporterDialogOpen(true);
        // Clear the param so reopening doesn't re-trigger
        searchParams.delete('editExporter');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [exporters, searchParams, setSearchParams]);

  // Dialog states
  const [addExporterDialogOpen, setAddExporterDialogOpen] = useState(false);
  const [editExporterDialogOpen, setEditExporterDialogOpen] = useState(false);
  const [editingExporter, setEditingExporter] = useState<ManagementExporter | null>(null);

  const [addWebsiteDialogOpen, setAddWebsiteDialogOpen] = useState(false);
  const [editWebsiteDialogOpen, setEditWebsiteDialogOpen] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState<ManagementWebsite | null>(null);

  const [installerDialogOpen, setInstallerDialogOpen] = useState(false);
  const [addChannelDialogOpen, setAddChannelDialogOpen] = useState(false);
  const [editChannelDialogOpen, setEditChannelDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ManagementNotificationChannel | null>(null);

  const [alertRuleDialogOpen, setAlertRuleDialogOpen] = useState(false);
  const [alertRuleDialogMode, setAlertRuleDialogMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [editingRule, setEditingRule] = useState<BackendAlertRule | null>(null);
  const [rulesVersion, setRulesVersion] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'exporter' | 'website' | 'channel' | 'rule';
    id: number;
    name: string;
  } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Silence dialog states
  const [createSilenceDialogOpen, setCreateSilenceDialogOpen] = useState(false);
  const [createSilenceLoading, setCreateSilenceLoading] = useState(false);
  const [preselectedExporterId, setPreselectedExporterId] = useState<number | undefined>();
  const [extendSilenceDialogOpen, setExtendSilenceDialogOpen] = useState(false);
  const [extendSilenceLoading, setExtendSilenceLoading] = useState(false);
  const [silenceToExtend, setSilenceToExtend] = useState<typeof silences[0] | null>(null);

  // Prometheus sync state
  const [syncing, setSyncing] = useState(false);

  // Alerts drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentExporter, setCurrentExporter] = useState<ManagementExporter | null>(null);

  // More actions menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<{
    type: 'exporter' | 'website';
    item: ManagementExporter | ManagementWebsite;
  } | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await exporterService.syncFromPrometheus();
      notifySuccess(`Synced ${result.synced} exporters from Prometheus`);
      await refetchExporters();
    } catch (err) {
      notifyError(extractApiError(err, 'Failed to sync from Prometheus'));
    } finally {
      setSyncing(false);
    }
  };

  const handleWebsiteClick = (website: ManagementWebsite) => {
    setEditingWebsite(website);
    setEditWebsiteDialogOpen(true);
  };

  const handleExporterClick = (exporter: ManagementExporter) => {
    setEditingExporter(exporter);
    setEditExporterDialogOpen(true);
  };

  const handleOpenAlertsDrawer = (exporter: ManagementExporter) => {
    setCurrentExporter(exporter);
    setDrawerOpen(true);
  };

  const handleOpenAlertsDrawerForWebsite = (website: ManagementWebsite) => {
    // Adapt website to exporter shape for the alerts drawer
    setCurrentExporter({
      id: website.id,
      name: website.url,
      displayName: website.name,
      status: website.status as 'up' | 'down' | 'warning',
      type: 'website',
      host: website.url,
      lastSeen: '-',
    });
    setDrawerOpen(true);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    type: 'exporter' | 'website',
    item: ManagementExporter | ManagementWebsite
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuTarget({ type, item });
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuTarget(null);
  };

  const handleMenuAction = (action: MenuAction) => {
    if (!menuTarget) return;

    switch (action) {
      case 'edit':
        if (menuTarget.type === 'exporter') {
          setEditingExporter(menuTarget.item as ManagementExporter);
          setEditExporterDialogOpen(true);
        } else {
          setEditingWebsite(menuTarget.item as ManagementWebsite);
          setEditWebsiteDialogOpen(true);
        }
        break;
      case 'manageAlerts':
        if (menuTarget.type === 'exporter') {
          handleOpenAlertsDrawer(menuTarget.item as ManagementExporter);
        }
        break;
      case 'addNotification':
        setActiveTab(2);
        setAddChannelDialogOpen(true);
        break;
      case 'openGrafana':
        // Find the raw exporter to get the Grafana dashboard UID
        const rawExp = rawExporters.find((e) => e.id === menuTarget.item.id);
        if (rawExp?.grafana_dashboard_uid) {
          window.open(
            `/grafana/d/${rawExp.grafana_dashboard_uid}`,
            '_blank'
          );
        } else {
          notifyWarning('No Grafana dashboard configured for this exporter');
        }
        break;
      case 'quickSilence':
        if (menuTarget.type === 'exporter') {
          const exporterItem = menuTarget.item as ManagementExporter;
          quickSilence(exporterItem.id, 60, 'Quick silence from UI')
            .then(() => {
              notifySuccess(`Silenced ${exporterItem.displayName} for 1 hour`);
              refetchSilences();
            })
            .catch((err) => {
              notifyError(`Failed to silence: ${extractApiError(err, 'Unknown error')}`);
            });
        }
        break;
      case 'disable':
        if (menuTarget.type === 'exporter') {
          const disableExporter = menuTarget.item as ManagementExporter;
          exporterService.updateExporter(disableExporter.id, { enabled: false })
            .then(() => {
              notifySuccess(`Disabled monitoring for ${disableExporter.displayName}`);
              refetchExporters();
            })
            .catch((err) => {
              notifyError(`Failed to disable monitoring: ${extractApiError(err, 'Unknown error')}`);
            });
        } else {
          const disableWebsite = menuTarget.item as ManagementWebsite;
          websiteApi.pauseWebsite(disableWebsite.id)
            .then(() => {
              notifySuccess(`Paused monitoring for ${disableWebsite.name}`);
              refetchWebsites();
            })
            .catch((err) => {
              notifyError(`Failed to pause monitoring: ${extractApiError(err, 'Unknown error')}`);
            });
        }
        break;
      case 'enable':
        if (menuTarget.type === 'exporter') {
          const enableExporter = menuTarget.item as ManagementExporter;
          exporterService.updateExporter(enableExporter.id, { enabled: true })
            .then(() => {
              notifySuccess(`Enabled monitoring for ${enableExporter.displayName}`);
              refetchExporters();
            })
            .catch((err) => {
              notifyError(`Failed to enable monitoring: ${extractApiError(err, 'Unknown error')}`);
            });
        } else {
          const enableWebsite = menuTarget.item as ManagementWebsite;
          websiteApi.resumeWebsite(enableWebsite.id)
            .then(() => {
              notifySuccess(`Resumed monitoring for ${enableWebsite.name}`);
              refetchWebsites();
            })
            .catch((err) => {
              notifyError(`Failed to resume monitoring: ${extractApiError(err, 'Unknown error')}`);
            });
        }
        break;
      case 'delete':
        setDeleteTarget({
          type: menuTarget.type,
          id: menuTarget.item.id,
          name:
            menuTarget.type === 'exporter'
              ? (menuTarget.item as ManagementExporter).displayName
              : (menuTarget.item as ManagementWebsite).name,
        });
        setDeleteDialogOpen(true);
        break;
    }

    handleMenuClose();
  };

  // ============================================================================
  // API Handlers
  // ============================================================================

  /**
   * Handle adding a new exporter
   */
  const handleAddExporter = async (data: AddExporterFormData): Promise<boolean> => {
    try {
      await exporterService.createExporter({
        name: data.displayName || `${data.host}:${data.port}`,
        target_host: `${data.host}:${data.port}`,
        prometheus_job: data.exporterType || 'node_exporter',
        customer_id: data.customerId || undefined,
        category_id: data.categoryId || undefined,
      });
      notifySuccess('Exporter added successfully');
      refetchExporters();
      return true;
    } catch (err) {
      console.error('Failed to add exporter:', err);
      notifyError(extractApiError(err, 'Failed to add exporter'));
      return false;
    }
  };

  /**
   * Handle testing exporter connection
   */
  const handleTestExporterConnection = async (data: {
    host: string;
    port: number;
    authType?: 'none' | 'basic' | 'bearer';
    authUsername?: string;
    authPassword?: string;
    authToken?: string;
  }) => {
    try {
      return await exporterService.testConnection(data);
    } catch (err) {
      console.error('Connection test failed:', err);
      return {
        success: false,
        error: extractApiError(err, 'Connection test failed'),
      };
    }
  };

  /**
   * Handle adding a new website
   */
  const handleAddWebsite = async (data: AddWebsiteFormData): Promise<boolean> => {
    try {
      await websiteApi.createWebsite({
        name: data.name,
        url: data.url,
        probe_type: data.probeType,
        customer_id: data.customerId,
        check_interval: data.checkInterval,
        port: data.port,
      });
      notifySuccess('Website added successfully');
      await refetchWebsites();
      return true;
    } catch (err) {
      console.error('Failed to add website:', err);
      notifyError(extractApiError(err, 'Failed to add website'));
      return false;
    }
  };

  /**
   * Handle testing website URL
   */
  const handleTestWebsiteUrl = async (url: string, probeType: 'http_2xx' | 'ssl_cert') => {
    try {
      return await websiteApi.testUrl(url, probeType);
    } catch (err) {
      console.error('URL test failed:', err);
      return {
        url,
        accessible: false,
        error: extractApiError(err, 'URL test failed'),
      };
    }
  };

  /**
   * Handle adding a notification channel
   */
  const handleAddNotificationChannel = async (
    data: AddNotificationChannelFormData
  ): Promise<boolean> => {
    try {
      // Build the value based on channel type
      let value = '';
      switch (data.type) {
        case 'canmail':
          value = String(data.canmailListId);
          break;
        case 'email':
          value = data.email || '';
          break;
        case 'slack':
          value = data.slackWebhookUrl || '';
          break;
        case 'teams':
          value = data.teamsWebhookUrl || '';
          break;
        case 'pagerduty':
          value = data.pagerdutyRoutingKey || '';
          break;
        case 'webhook':
          value = data.webhookUrl || '';
          break;
      }

      const createData: CreateNotificationChannelData = {
        type: data.type,
        name: data.name,
        value,
        watch_all: data.watchAll,
        watch_exporter_ids: data.watchExporterIds,
        severities: data.severities,
        config: {
          slackChannel: data.slackChannel,
          webhookSecret: data.webhookSecret,
        },
      };

      await createChannel(createData);
      notifySuccess('Notification channel added successfully');
      return true;
    } catch (err) {
      console.error('Failed to add notification channel:', err);
      notifyError(extractApiError(err, 'Failed to add notification channel'));
      return false;
    }
  };

  /**
   * Handle editing a notification channel
   */
  const handleEditChannel = (channel: ManagementNotificationChannel) => {
    setEditingChannel(channel);
    setEditChannelDialogOpen(true);
  };

  /**
   * Handle saving notification channel edits
   */
  const handleSaveChannelEdit = async (
    id: number,
    data: EditNotificationChannelFormData
  ): Promise<boolean> => {
    try {
      await updateChannel(id, {
        type: data.type,
        name: data.name,
        value: data.value,
        watch_all: data.watchAll,
        watch_exporter_ids: data.watchExporterIds,
        severities: data.severities,
      });
      notifySuccess('Notification channel updated successfully');
      return true;
    } catch (err) {
      console.error('Failed to update notification channel:', err);
      notifyError(extractApiError(err, 'Failed to update notification channel'));
      return false;
    }
  };

  /**
   * Handle testing a notification channel
   */
  const handleTestChannel = async (id: number): Promise<{ success: boolean; message: string; deliveryTime?: number }> => {
    try {
      const result = await testChannel(id);
      return result;
    } catch (err) {
      console.error('Failed to test notification channel:', err);
      return {
        success: false,
        message: extractApiError(err, 'Test failed')
      };
    }
  };

  /**
   * Handle channel verified callback
   */
  const handleChannelVerified = (id: number) => {
    // Refetch channels to update verified status
    refetchChannels();
    notifySuccess('Channel verified successfully');
  };

  /**
   * Handle creating a new alert rule
   */
  const handleCreateRule = async (data: AlertRuleFormData): Promise<boolean> => {
    try {
      await ruleService.createRule(data);
      notifySuccess('Alert rule created successfully');
      setRulesVersion(v => v + 1);
      return true;
    } catch (err) {
      notifyError(extractApiError(err, 'Failed to create alert rule'));
      return false;
    }
  };

  const handleBatchCreateRules = async (rules: AlertRuleFormData[]): Promise<boolean> => {
    try {
      let created = 0;
      for (const data of rules) {
        try {
          await ruleService.createRule(data);
          created++;
        } catch (err) {
          console.error(`Failed to create rule "${data.name}":`, err);
        }
      }
      if (created > 0) {
        notifySuccess(`${created} alert rule${created > 1 ? 's' : ''} created successfully`);
        setRulesVersion(v => v + 1);
      }
      if (created < rules.length) {
        notifyError(`${rules.length - created} rule(s) failed to create`);
      }
      return created === rules.length;
    } catch (err) {
      notifyError(extractApiError(err, 'Failed to create alert rules'));
      return false;
    }
  };

  /**
   * Handle saving an existing alert rule
   */
  const handleSaveRule = async (data: AlertRuleFormData): Promise<boolean> => {
    if (!editingRule) return false;
    try {
      await ruleService.updateRule(editingRule.id, data);
      notifySuccess('Alert rule updated successfully');
      setRulesVersion(v => v + 1);
      return true;
    } catch (err) {
      notifyError(extractApiError(err, 'Failed to update alert rule'));
      return false;
    }
  };

  /**
   * Handle delete confirmation
   */
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    try {
      switch (deleteTarget.type) {
        case 'exporter':
          await exporterService.deleteExporter(deleteTarget.id);
          refetchExporters();
          break;
        case 'website':
          await websiteApi.deleteWebsite(deleteTarget.id);
          refetchWebsites();
          break;
        case 'channel':
          await deleteChannelApi(deleteTarget.id);
          break;
        case 'rule':
          await ruleService.deleteRule(deleteTarget.id);
          await ruleService.syncRules();
          setRulesVersion(v => v + 1);
          break;
      }
      notifySuccess(`${deleteTarget.type} deleted successfully`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(`Failed to delete ${deleteTarget.type}:`, err);
      notifyError(extractApiError(err, `Failed to delete ${deleteTarget.type}`));
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Handle deleting a notification channel
   */
  const handleDeleteChannel = (channel: ManagementNotificationChannel) => {
    setDeleteTarget({
      type: 'channel',
      id: channel.id,
      name: channel.name,
    });
    setDeleteDialogOpen(true);
  };

  /**
   * Handle generating installer
   */
  const handleGenerateInstaller = async (data: InstallerFormData) => {
    try {
      const response = await installerService.generate(data);
      const installer = response?.installer;

      if (!installer) {
        console.error('Failed to generate installer: unexpected response shape', response);
        notifyError('Failed to generate installer: unexpected server response');
        return null;
      }

      notifySuccess('Installer generated successfully');

      return {
        token: installer.token,
        downloadUrl: installer.downloadUrl,
        expiresAt: installer.expiresAt,
        maxUses: installer.uses,
      };
    } catch (err) {
      console.error('Failed to generate installer:', err);
      notifyError(extractApiError(err, 'Failed to generate installer'));
      return null;
    }
  };

  // ============================================================================
  // Silence Handlers
  // ============================================================================

  /**
   * Handle creating a new silence
   */
  const handleCreateSilence = async (data: Parameters<typeof createSilence>[0]) => {
    setCreateSilenceLoading(true);
    try {
      await createSilence(data);
      notifySuccess('Silence created successfully');
      setCreateSilenceDialogOpen(false);
      setPreselectedExporterId(undefined);
    } catch (err) {
      notifyError(`Failed to create silence: ${extractApiError(err, 'Unknown error')}`);
    } finally {
      setCreateSilenceLoading(false);
    }
  };

  /**
   * Handle extending a silence
   */
  const handleExtendSilence = async (id: number, durationMinutes: number) => {
    setExtendSilenceLoading(true);
    try {
      await extendSilence(id, durationMinutes);
      notifySuccess('Silence extended successfully');
      setExtendSilenceDialogOpen(false);
      setSilenceToExtend(null);
    } catch (err) {
      notifyError(`Failed to extend silence: ${extractApiError(err, 'Unknown error')}`);
    } finally {
      setExtendSilenceLoading(false);
    }
  };

  /**
   * Handle deleting a silence
   */
  const handleDeleteSilence = async (id: number) => {
    try {
      await deleteSilence(id);
      notifySuccess('Silence deleted successfully');
    } catch (err) {
      notifyError(`Failed to delete silence: ${extractApiError(err, 'Unknown error')}`);
    }
  };

  /**
   * Open extend dialog for a silence
   */
  const handleOpenExtendDialog = (id: number) => {
    const silence = silences.find((s) => s.id === id);
    if (silence) {
      setSilenceToExtend(silence);
      setExtendSilenceDialogOpen(true);
    }
  };

  /**
   * Open create silence dialog (optionally with preselected exporter)
   */
  const handleOpenCreateSilenceDialog = (exporterId?: number) => {
    setPreselectedExporterId(exporterId);
    setCreateSilenceDialogOpen(true);
  };

  // ============================================================================
  // Filter data based on search
  // ============================================================================

  const filteredExporters = exporters.filter(
    (e) =>
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.host.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredWebsites = websites.filter(
    (w) =>
      w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 48,
            height: 48,
          }}
        >
          <SettingsIcon />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure monitoring targets and notification settings
          </Typography>
        </Box>
      </Box>

      {/* Error Alerts */}
      {exportersError && activeTab === 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => refetchExporters()}>
          Failed to load exporters: {exportersError}
        </Alert>
      )}
      {websitesError && activeTab === 1 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => refetchWebsites()}>
          Failed to load websites: {websitesError}
        </Alert>
      )}
      {channelsError && activeTab === 2 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => refetchChannels()}>
          Failed to load notification channels: {channelsError}
        </Alert>
      )}
      {silencesError && activeTab === 3 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => refetchSilences()}>
          Failed to load silences: {silencesError}
        </Alert>
      )}

      {/* Search Bar - only show for Exporters/Websites tabs */}
      {activeTab < 2 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search across exporters and websites..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      )}

      {/* Tabs Container */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<ComputerIcon />}
            iconPosition="start"
            label={`Exporters (${exportersLoading ? '...' : filteredExporters.length})`}
          />
          <Tab
            icon={<WebsiteIcon />}
            iconPosition="start"
            label={`Websites (${websitesLoading ? '...' : filteredWebsites.length})`}
          />
          <Tab
            icon={<NotificationsIcon />}
            iconPosition="start"
            label={`Notifications (${channelsLoading ? '...' : notificationChannels.length})`}
          />
          <Tab
            icon={<SilenceIcon />}
            iconPosition="start"
            label={`Silences (${silencesLoading ? '...' : silences.length})`}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <ExportersTab
              exporters={filteredExporters}
              loading={exportersLoading}
              onOpenAlertsDrawer={handleOpenAlertsDrawer}
              onExporterClick={handleExporterClick}
              onMenuOpen={handleMenuOpen}
              onAddExporter={() => setAddExporterDialogOpen(true)}
              onGenerateInstaller={() => setInstallerDialogOpen(true)}
              onSync={handleSync}
              syncing={syncing}
            />
          )}

          {activeTab === 1 && (
            <WebsitesTab
              websites={filteredWebsites}
              rawWebsites={rawWebsites}
              summary={websiteSummary}
              loading={websitesLoading}
              onMenuOpen={handleMenuOpen}
              onAddWebsite={() => setAddWebsiteDialogOpen(true)}
              onOpenAlertsDrawer={handleOpenAlertsDrawerForWebsite}
              onWebsiteClick={handleWebsiteClick}
            />
          )}

          {activeTab === 2 && (
            <NotificationsTab
              channels={notificationChannels}
              loading={channelsLoading}
              onAddChannel={() => setAddChannelDialogOpen(true)}
              onEditChannel={handleEditChannel}
              onDeleteChannel={handleDeleteChannel}
              onTestChannel={handleTestChannel}
              onChannelVerified={handleChannelVerified}
            />
          )}

          {activeTab === 3 && (
            <SilencesTab
              silences={silences}
              loading={silencesLoading}
              onCreateSilence={() => handleOpenCreateSilenceDialog()}
              onExtend={handleOpenExtendDialog}
              onDelete={handleDeleteSilence}
            />
          )}
        </Box>
      </Paper>

      {/* More Actions Menu */}
      <MoreActionsMenu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        targetType={menuTarget?.type || 'exporter'}
        targetId={menuTarget?.item?.id || 0}
        targetName={
          menuTarget?.type === 'exporter'
            ? (menuTarget.item as ManagementExporter).displayName
            : (menuTarget?.item as ManagementWebsite)?.name || ''
        }
        isDisabled={
          menuTarget?.type === 'exporter'
            ? (menuTarget.item as ManagementExporter).enabled === false
            : (menuTarget?.item as ManagementWebsite)?.enabled === false
        }
        onAction={handleMenuAction}
      />

      {/* Alerts Drawer */}
      <AlertsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        exporter={currentExporter}
        rulesVersion={rulesVersion}
        onCreateRule={() => {
          setAlertRuleDialogMode('create');
          setEditingRule(null);
          setAlertRuleDialogOpen(true);
        }}
        onEditRule={(rule) => {
          setAlertRuleDialogMode('edit');
          setEditingRule(rule);
          setAlertRuleDialogOpen(true);
        }}
        onDeleteRule={(rule) => {
          setDeleteTarget({ type: 'rule', id: rule.id, name: rule.name });
          setDeleteDialogOpen(true);
        }}
        onDuplicateRule={(rule) => {
          setAlertRuleDialogMode('duplicate');
          setEditingRule(rule);
          setAlertRuleDialogOpen(true);
        }}
      />

      {/* Dialogs */}
      <AddExporterDialog
        open={addExporterDialogOpen}
        onClose={() => setAddExporterDialogOpen(false)}
        onAdd={handleAddExporter}
        categories={categories}
        customers={customers}
        existingHosts={rawExporters.map((e) => e.target_host)}
      />

      <AddWebsiteDialog
        open={addWebsiteDialogOpen}
        onClose={() => setAddWebsiteDialogOpen(false)}
        onAdd={handleAddWebsite}
        customers={customers}
        existingUrls={rawWebsites.map((w) => w.url)}
      />

      <EditExporterDialog
        open={editExporterDialogOpen}
        onClose={() => {
          setEditExporterDialogOpen(false);
          setEditingExporter(null);
        }}
        onSave={async (id, data) => {
          try {
            await exporterService.updateExporter(id, {
              display_name: data.displayName,
              description: data.description,
              network: data.network,
              customer_id: data.customerId ?? undefined,
              category_id: data.categoryId ?? undefined,
              enabled: data.enabled,
            });
            notifySuccess('Exporter updated successfully');
            refetchExporters();
            return true;
          } catch (err) {
            console.error('Failed to update exporter:', err);
            notifyError(extractApiError(err, 'Failed to update exporter'));
            return false;
          }
        }}
        exporter={editingExporter ? rawExporters.find((e) => e.id === editingExporter.id) || null : null}
        categories={categories}
        customers={customers}
      />

      <EditWebsiteDialog
        open={editWebsiteDialogOpen}
        onClose={() => {
          setEditWebsiteDialogOpen(false);
          setEditingWebsite(null);
        }}
        onSave={async (id, data) => {
          try {
            await websiteApi.updateWebsite(id, {
              name: data.name,
              customer_id: data.customerId ?? undefined,
              check_interval: data.checkInterval,
            });
            notifySuccess('Website updated successfully');
            await refetchWebsites();
            return true;
          } catch (err) {
            console.error('Failed to update website:', err);
            notifyError(extractApiError(err, 'Failed to update website'));
            return false;
          }
        }}
        website={editingWebsite}
        customers={customers}
      />

      <AddNotificationChannelDialog
        open={addChannelDialogOpen}
        onClose={() => setAddChannelDialogOpen(false)}
        onAdd={handleAddNotificationChannel}
        exporters={exporters}
      />

      <EditNotificationChannelDialog
        open={editChannelDialogOpen}
        onClose={() => {
          setEditChannelDialogOpen(false);
          setEditingChannel(null);
        }}
        onSave={handleSaveChannelEdit}
        channel={editingChannel}
        exporters={exporters}
        canmailLists={canmailLists}
      />

      <InstallerGeneratorDialog
        open={installerDialogOpen}
        onClose={() => setInstallerDialogOpen(false)}
        onGenerate={handleGenerateInstaller}
        customers={customers}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title={`Delete ${deleteTarget?.type || 'item'}?`}
        itemName={deleteTarget?.name || ''}
        itemType={deleteTarget?.type || 'exporter'}
        loading={deleteLoading}
      />

      {/* Alert Rule Dialog */}
      <AlertRuleDialog
        open={alertRuleDialogOpen}
        onClose={() => { setAlertRuleDialogOpen(false); setEditingRule(null); }}
        onSave={alertRuleDialogMode === 'edit' ? handleSaveRule : handleCreateRule}
        onBatchSave={handleBatchCreateRules}
        mode={alertRuleDialogMode}
        rule={editingRule}
        exporterType={currentExporter?.type || null}
      />

      {/* Silence Dialogs */}
      <CreateSilenceDialog
        open={createSilenceDialogOpen}
        onClose={() => {
          setCreateSilenceDialogOpen(false);
          setPreselectedExporterId(undefined);
        }}
        onSubmit={handleCreateSilence}
        exporters={rawExporters}
        preselectedExporterId={preselectedExporterId}
        loading={createSilenceLoading}
      />

      <ExtendSilenceDialog
        open={extendSilenceDialogOpen}
        onClose={() => {
          setExtendSilenceDialogOpen(false);
          setSilenceToExtend(null);
        }}
        onSubmit={handleExtendSilence}
        silence={silenceToExtend}
        loading={extendSilenceLoading}
      />

    </Box>
  );
}

export default ManagementV2;
